import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Modal, CircularProgress, Link, Card } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AttachmentIcon from '@mui/icons-material/Attachment';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import { Header } from '../components/Header';
import useUserStore from '../store/UserStore';
import { getNotifications } from '../services/getNotifications';
import { markAsRead } from '../services/markAsRead';
import { saveUserAction } from '../services/saveUserAction';
import { useTranslation } from 'react-i18next';

const NotificationCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(1),
  padding: theme.spacing(2),
  cursor: 'pointer',
  position: 'relative',
  backgroundColor: '#fff',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
  overflow: 'hidden',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
  },
}));

const UnreadIndicator = styled('span')(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: "#50A579",
}));

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 600,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[5],
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  maxHeight: '80vh',
  overflowY: 'auto',
}));

export function Comunicados({ setIsAuthenticated }) {
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const user = useUserStore((state) => state.user);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    fetchNotifications();
  }, [user.idpersona]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await getNotifications();

      if (response.data.message) {
        setLoading(false);
        return;
      }

      // Generate a new value for each notification to delete the html tags
      response.data.forEach((notification) => {
        notification.freehtmlcontent = notification.content.replace(/<[^>]*>?/gm, '');
      });

      setNotifications(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setLoading(false);
    }
  };

  const handleOpenModal = (notification) => {
    setSelectedNotification(notification);
    saveUserAction({ id_accion: 2, detalles_accion: `Accessed to the notification with id ${notification.id}` });

    setNotifications((prevNotifications) =>
      prevNotifications.map((n) =>
        n.id_asignacion === notification.id_asignacion
          ? { ...n, read: true, read_at: new Date() }
          : n
      )
    );

    if (!notification.read) {
      markAsRead(notification.id)
        .catch((error) => {
          console.error('Error marking as read:', error);
          setNotifications((prevNotifications) =>
            prevNotifications.map((n) =>
              n.id_asignacion === notification.id_asignacion
                ? { ...n, read: false, read_at: null }
                : n
            )
          );
        });
    }
  };

  const handleCloseModal = () => {
    setSelectedNotification(null);
  };

  const renderAttachment = (attachment) => {
    switch (attachment.type) {
      case 'pdf':
        return (
          <Box key={attachment.url} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PictureAsPdfIcon sx={{ color: '#FF5722', fontSize: 40, mr: 1 }} />
            <Link href={attachment.url} download target="_blank" rel="noopener">
              {attachment.name}
            </Link>
          </Box>
        );
      case 'image':
        return (
          <Box key={attachment.url} sx={{ mb: 2 }}>
            <img
              src={attachment.url}
              alt={attachment.name}
              style={{ maxWidth: '100%', height: 'auto' }}
            />
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {attachment.name}
            </Typography>
          </Box>
        );
      case 'document':
        return (
          <Box key={attachment.url} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <DescriptionIcon sx={{ color: '#3f51b5', fontSize: 40, mr: 1 }} />
            <Link href={attachment.url} download target="_blank" rel="noopener">
              {attachment.name}
            </Link>
          </Box>
        );
      default:
        return (
          <Typography key={attachment.url} variant="body2">
            '{attachment.name}'
          </Typography>
        );
    }
  };

  return (
    loading ? 
    (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress sx={{ 
        color: 'rgba(80, 165, 121, 0.7)',
       }}/>
    </Box>) :

    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <Header setIsAuthenticated={setIsAuthenticated} />
      <Box sx={{ padding: 2 }}>
        <Typography variant="h4" sx={{ mb: 4, color: 'black', textAlign: 'center' }}>
          {t('notifications.1title')}<span style={{ color: '#50A579' }}>{t('notifications.2title')}</span>
        </Typography>
        {notifications.length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: 'center' }}>
            {t('notifications.noNotifications')}
          </Typography>
        ) : (
          notifications.map((notification) => (
            <NotificationCard
              key={notification.id_asignacion}
              onClick={() => handleOpenModal(notification)}
            >
              {!notification.read && <UnreadIndicator />}
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {notification.title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {notification.freehtmlcontent.substring(0, 100)}
                {notification.freehtmlcontent.length > 100 && '...'}
              </Typography>
              {notification.attachments && notification.attachments.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <AttachmentIcon fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" sx={{ color: '#50A579' }}>
                    {notification.attachments.length === 1 ? 'Adjunt' : `${notification.attachments.length} Adjuntos`}
                  </Typography>
                </Box>
              )}
            </NotificationCard>
          ))
        )}
      </Box>
      <Modal
        open={!!selectedNotification}
        onClose={handleCloseModal}
        aria-labelledby="notification-modal"
        aria-describedby="notification-description"
      >
        <ModalContent>
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography id="notification-modal" variant="h6" component="h2" sx={{ mb: 2 }}>
            {selectedNotification?.title}
          </Typography>
          <Typography id="notification-description" sx={{ mt: 2 }} dangerouslySetInnerHTML={{ __html: selectedNotification?.content }}>
          </Typography>
          {selectedNotification?.attachments && selectedNotification.attachments.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Archivos Adjuntos:
              </Typography>
              {selectedNotification.attachments.map((attachment) => renderAttachment(attachment))}
            </Box>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
}
